/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue'
import moment from 'moment'
import _ from 'lodash';
import WyFilter from '../../library/wy-filter';
import WyConstants from '../../library/wy-constants.js';

const state = {
  changed: 0,
  currentNavMenu: null,
  currentPage: null,
  currentPageInfo: {
    title: null,
    subtitle: null,
    tooltip: null
  },
  currentPageName: null, 
  noDataRoutePath: null,
  noDataType: 1,
  pages: {},
  shareState: {
    dateStart: null,
    dateEnd: null,
    dateHasChanged: false,
    warning: false,
    warningMessages: [],
    unselectedFilterValues: {},
    apiVersion: null
  },
  noAvaliableData: false,
  changedFilters: [],
  filterChangedStatus: false
}

const getters = {
  pages: state => state.pages,
  currentNavMenu: state => state.currentNavMenu,
  currentPage: state => state.currentPage,
  currentPageInfo: state => state.currentPageInfo,
  currentPageName: state => state.currentPageName,
  shareState: state => state.shareState,
  noDataRoutePath: state => state.noDataRoutePath,
  noDataType: state => state.noDataType,
  noAvaliableData: state => state.noAvaliableData,
  changedFilters: state => state.changedFilters,
  filterChangedStatus: state => state.filterChangedStatus,

  dataModel: state => {
    return (state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) ? state.pages[state.currentPage].dataModel : {}
  },

  currentPagePanels: state => {
    return function (pagePanels) {
      try {
        let userData = localStorage.getItem('userData');
        if(!userData) {
          return pagePanels;
        }
        else {
          userData = JSON.parse(userData);
        }
        
        if(!userData[state.currentPageName]) {
          return pagePanels;
        }

        let panels = userData[state.currentPageName].panels;
        if(panels && panels.length > 0 && panels[0] != "") {
          const panelsDiff = _.difference(pagePanels, panels);
          if(panelsDiff.length > 0) {
            panels.push(...panelsDiff);
          }
          return panels;
        }
      }
      catch(e) {
        console.error(e);
      }
      return pagePanels;
    }
  },

  // ******************** new filter getters ********************
  transfersVisible: state => {
    return (state.pages[state.currentPage].dataModel.filters.sources.value.includes('BotToLC') || state.pages[state.currentPage].dataModel.filters.sources.value.includes('LCOnly'));
  },

  containmentVisible: state => {
    return state.pages[state.currentPage].dataModel.filters.sources.value.includes('BotOnlyContained');
  },

  moreFilters: state => Object.keys(state.pages[state.currentPage].dataModel.filters).reduce((list, name) => {    
    if(state.pages[state.currentPage].dataModel.currFilterNames.includes(name) && state.pages[state.currentPage].dataModel.filters[name].category == 'moreFilters') {
      list.push(state.pages[state.currentPage].dataModel.filters[name]);
    }
    return list;
  }, []),
  
  moreFiltersCounter: state => {
    let count = 0;
    const filters = getters.moreFilters(state)
    const defaultCustTypeFilters = filters && filters.find(filter => filter.name == "sources")
    const custTypesDataSource = defaultCustTypeFilters && defaultCustTypeFilters.dataSource && defaultCustTypeFilters.dataSource.map((filter) => filter.key)
    let changedFilters = [];
    if (defaultCustTypeFilters) {
      if (!_.isEqual(_.sortBy(custTypesDataSource), _.sortBy(defaultCustTypeFilters.value))) {
        if (count === 0) count = 1;
        else count++;
        changedFilters.push('sources')
      } 
    }

    for(const filter of filters) {
      if (filter.name == "sources") continue;
      if(!WyFilter.compareValues(filter.value, filter.valueOrigin) || !WyFilter.compareValues(filter.valueTo, filter.valueOriginTo)) {
        count++;
        changedFilters.push(filter.name);
      }
    }
    state.changedFilters = changedFilters.sort();
    return count;
  },

  moreFiltersChanged: state => {
    let hasChanged = false;
    for(const filter of getters.moreFilters(state)) {
      if(!WyFilter.compareValues(filter.value, filter.previousValue) || !WyFilter.compareValues(filter.valueTo, filter.previousValueTo)) {
        hasChanged = true;
        break;
      }
    }
    return hasChanged;
  },

  dateCompareRange: state => {
    let dateStart = state.pages[state.currentPage].dataModel.dateStart;
    let dateEnd = state.pages[state.currentPage].dataModel.dateEnd;
    if(dateStart && dateEnd) {
      let dateStartMoment = moment(dateStart,'YYYY-MM-DD');
      let dateEndMoment = moment(dateEnd,'YYYY-MM-DD');
      let numOfDaysInRange = dateEndMoment.diff(dateStartMoment, 'd')+1
      return {
        numOfDaysInRange: numOfDaysInRange,
        dateStart: dateStartMoment.format("YYYY-MM-DD"),
        dateEnd: dateEndMoment.format("YYYY-MM-DD"),
        dateStartCompare: dateStartMoment.subtract(numOfDaysInRange, 'day').format("YYYY-MM-DD"),
        dateEndCompare: dateEndMoment.subtract(numOfDaysInRange, 'day').format("YYYY-MM-DD")
      }
    }
    return null;
  },
}

const mutations = {
  setCurrentPage(state, {pageName, bindModel}) {
    state.currentPageName = pageName;
    state.currentPage = bindModel;
    if(!state.pages[state.currentPage]) {
      Vue.set(state.pages, state.currentPage, createPageModel())
    }
  },

  setCurrentPageInfo(state, {title, subtitle, tooltip}) {
    state.currentPageInfo = {
      title: title,
      subtitle: subtitle,
      tooltip: tooltip
    }
  },

  // ******************** data model ********************
  addDataModelSortings(state, sortings) {
    let page = state.pages[state.currentPage];
    if(page && state.pages[state.currentPage].dataModel && page.dataModel.sortings.length == 0) {
      state.pages[state.currentPage].dataModel.sortings = sortings
    }
  },

  updateDataModelSorting(state, {name, direction}) {
    if(state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) {
      state.pages[state.currentPage].dataModel.sortField = name;
      state.pages[state.currentPage].dataModel.sortDirection = direction;
    }
  },

  addDataModelColumns(state, columns) {
    let page = state.pages[state.currentPage];
    if(page && state.pages[state.currentPage].dataModel && page.dataModel.columns.length == 0) {
      columns = columns.filter(column => this.getters.isRoleAllow(column.perms, true))
      columns.map(el => { if(el.selected==null) el.selected=false; if(el.mandatory) el.selected=true; })
      state.pages[state.currentPage].dataModel.columns = JSON.parse(JSON.stringify(columns));
    }
  },

  updateDataModelColumns(state, {columns, columnsConfig}) {
    if(state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) {
      if(columnsConfig) {
        const hideColumns = Object.keys(columnsConfig).filter(column =>  !columnsConfig[column]);
        columns = columns.filter(column => (this.getters.isRoleAllow(column.perms, true) && (!hideColumns.includes(column.name))));
        
        // get all percent change types
        const allPercentChangeColumns = columns.filter(column => column.type == 'percentChange');
        allPercentChangeColumns.map(percentColumn => {
          // get all columns under this percent column group          
          const percentColumns = columns.filter(col => (col.name!=percentColumn.name && col.group == percentColumn.group));
          if(percentColumns.length == 0) {
            hideColumns.push(percentColumn.name);
          }
        });

        // filter out percent change column if the group columns are hidden
        columns = columns.filter(column => (this.getters.isRoleAllow(column.perms, true) && (!hideColumns.includes(column.name))));
      }
      else {
        columns = columns.filter(column => this.getters.isRoleAllow(column.perms, true))
      }
      columns.map(el => { if(el.selected==null) el.selected=false; if(el.mandatory) el.selected=true; })
      state.pages[state.currentPage].dataModel.columns = JSON.parse(JSON.stringify(columns));
    }
  },

  addDataModelActions(state, actions) {
    let page = state.pages[state.currentPage];
    if(page && state.pages[state.currentPage].dataModel && page.dataModel.actions.length == 0) {
      state.pages[state.currentPage].dataModel.actions = actions
    }
  },

  updateDataModelPageNumber(state, val) {
    if(state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) {
      state.pages[state.currentPage].dataModel.page.number = val;
    }
  },

  updateDataModelPageSize(state, val) {
    if(state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) {
      state.pages[state.currentPage].dataModel.page.number = 1;
      state.pages[state.currentPage].dataModel.page.size = val;
    }
  },

  updateDataModelPagination(state, val) {
    if(state.pages[state.currentPage] && state.pages[state.currentPage].dataModel) {
      state.pages[state.currentPage].dataModel.page.pagination = val;
    }
  },

  resetDataModel(state) {
    state.pages[state.currentPage].dataModel = createDataModelObj();
  },

  updateChanged(state) {
    state.changed++;
  },

  getFilterChangedStatus(state, value) {
    state.filterChangedStatus = value;
  },

  updataNoDataType(state, type) {
    state.noDataType = type;
  },

  updateNoAvaliableData(state, value) {
    state.noAvaliableData = value;
  },

  updateSite(state, {navMenu}) {
    state.currentNavMenu = navMenu
  },

  updateShareState(state, keyValuPair) {
    state.shareState[keyValuPair.key] = keyValuPair.value;
  }
}

const actions = {
  setCurrentPage({ commit }, data) {
    commit('setCurrentPage', data);
  },
  
  removeSiteSession() {
    window.sessionStorage.removeItem(WyConstants.SESSION_SITE_KEY);
  },

  checkHasFilter({state, commit}, {toPage, toPath}) {    
    const toPageObj = state.pages[toPage];
    if(toPageObj) {
      state.noDataRoutePath = toPageObj.dataModel.hasFilter ? null : toPath;
      commit('updateChanged');
      return toPageObj.dataModel.hasFilter;
    }
    else {
      return false;
    }
  },

  // ******************** new filter actions ********************
  initFilterOriginalSource({ getters }) {
    // original values
    const filters = getters.dataModel.filters;
    for(let name in filters) {
      const filter = filters[name];
      if(filter.dataSource && Array.isArray(filter.dataSource)) {
        //filter.originalSrouce = filter.dataSource.map(item => item.key);
        if(filter.value == null) filter.value = [];
      }
      filter.valueOrigin = _.clone(filter.value);
      filter.valueOriginTo = _.clone(filter.valueTo);
    }
  },

  async initFilterDataSource({ state, rootState, getters }, {filterNames, fromPage, toPage}) {
    getters.dataModel.currFilterNames = filterNames;
    state.shareState.warningMessages = [];

    const isDiffBindModel = (fromPage != null && toPage != fromPage);
    const fromPageObj = state.pages[fromPage];
    const toPageObj = state.pages[toPage];

    // set date range
    if(toPageObj.dataModel.dateMin && toPageObj.dataModel.dateMax) {
      let minDateObj = moment(toPageObj.dataModel.dateMin,'YYYY-MM-DD');
      let maxDateObj = moment(toPageObj.dataModel.dateMax,'YYYY-MM-DD');

      let previousSelectedDateRange = null;
      if(fromPageObj && fromPageObj.dataModel.dateCalendar && fromPageObj.dataModel.dateCalendar.dateRange.start != "" && fromPageObj.dataModel.dateCalendar.dateRange.end != "") {
        previousSelectedDateRange = fromPageObj.dataModel.dateCalendar.dateRange;
      }

      // prepare date range selections
      toPageObj.dataModel.dateRangeOptions = WyFilter.dateRangeOptions();
      toPageObj.dataModel.dateRangeOptions.map(rangeOption => {
        if(rangeOption.start && rangeOption.start.isSameOrBefore(maxDateObj)) {
          rangeOption.disabled = false;
          if(rangeOption.start.isBefore(minDateObj)) {
            rangeOption.start = moment(minDateObj.format('YYYY-MM-DD'));
          }
          if(rangeOption.end.isAfter(maxDateObj)) {
            rangeOption.end = moment(maxDateObj.format('YYYY-MM-DD'));
          }
        }
      })

      if(previousSelectedDateRange) {
        // apply previous selected date range
        toPageObj.dataModel.dateStart = fromPageObj.dataModel.dateStart;
        toPageObj.dataModel.dateEnd = fromPageObj.dataModel.dateEnd;
        toPageObj.dataModel.dateRangeType = fromPageObj.dataModel.dateRangeType;
        toPageObj.dataModel.dateCalendar.dateRange = previousSelectedDateRange;
      }
      else {
        if(!toPageObj.dataModel.dateStart) {
          const selectedDateRange = (rootState.auth.companyConfig.default || {}).selectedDateRange || null;

          let startDate = (selectedDateRange && selectedDateRange.startDate) ? moment(selectedDateRange.startDate) : null; // set last 30 day by default
          let endDate = (selectedDateRange && selectedDateRange.endDate) ? moment(selectedDateRange.endDate) : null;

          if(!startDate || startDate.isBefore(minDateObj) || startDate.isAfter(maxDateObj)) {
            const targetDate = moment(maxDateObj).add(-29, 'days');
            if(targetDate.isBefore(minDateObj)) {
              startDate = moment(minDateObj);
            }
            else {
              startDate = targetDate;
            }
          }

          if(!endDate || endDate.isAfter(maxDateObj) || endDate.isBefore(minDateObj)) {
            endDate = moment(maxDateObj);
          }
          else {
            moment(maxDateObj);
          }

          toPageObj.dataModel.dateStart = startDate.format('YYYY-MM-DD');
          toPageObj.dataModel.dateEnd = endDate.format('YYYY-MM-DD');
          toPageObj.dataModel.dateRangeType = -1;
          toPageObj.dataModel.dateCalendar.dateRange.start = toPageObj.dataModel.dateStart;
          toPageObj.dataModel.dateCalendar.dateRange.end = toPageObj.dataModel.dateEnd;
        }
      }

      // popup a message if the selected date range is not inclusive
      if(isDiffBindModel) {
        let dateStartMoment = moment(toPageObj.dataModel.dateCalendar.dateRange.start,'YYYY-MM-DD');
        let dateEndMoment = moment(toPageObj.dataModel.dateCalendar.dateRange.end,'YYYY-MM-DD');
        let isDateStartInclusive = dateStartMoment.isBetween(minDateObj, maxDateObj, 'date', '[]');
        let isDateEndInclusive = dateEndMoment.isBetween(minDateObj, maxDateObj, 'date', '[]');
        if(!isDateStartInclusive || !isDateEndInclusive) {
          state.shareState.warningMessages.push(`Data is available for time period: ${minDateObj.format("MMM DD, YYYY")} to ${maxDateObj.format("MMM DD, YYYY")}`);
        }
      }

      toPageObj.dataModel.hasFilter = true;
    }
    else {
      toPageObj.dataModel.hasFilter = false;
    }
    
    const filters = toPageObj.dataModel.filters;
    for(let name of filterNames) {
      const filter = filters[name];
      if(filter) {
        // filter out the unselectedFilterValues from eixsting value
        if(isDiffBindModel) {
          const unselectedFilterValues = getters.shareState.unselectedFilterValues[name];
          if(Array.isArray(filter.value) && filter.value.length > 0 && unselectedFilterValues && unselectedFilterValues.length > 0) {
            filter.value = filter.dataSource.map(item => item.key).filter(key => !unselectedFilterValues.includes(key));
          }

          if(fromPage && Array.isArray(filter.value)) {
            const fromPageFilter = state.pages[fromPage].dataModel.filters[name];
            if(fromPageFilter && fromPageFilter.dataSource && Array.isArray(fromPageFilter.value)) {
              const notInThisFilter = fromPageFilter.value.filter(val => !filter.value.includes(val))
              if(notInThisFilter.length > 0) {
                const fromPageFilterDataSource = fromPageFilter.dataSource.filter(({key}) => notInThisFilter.includes(key));
                const fromPageFilterValues = fromPageFilterDataSource.map(item => item.label);
                if(fromPageFilterValues.length > 0) {
                  state.shareState.warningMessages.push(`Data is not available for ${filter.label} ${fromPageFilterValues.join(', ')}`);
                }
              }
            }
          }
        }

        // previouse seleted values
        filter.previousValue = _.clone(filter.value);
        filter.previousValueTo = _.clone(filter.valueTo);
      }
    }
    state.shareState.warning = state.shareState.warningMessages.length > 0;

    // enable/disable filter
    for(const name in filters) {
      filters[name].isDisabled = !filterNames.includes(name);
    }
  },

  async checkFilterChanged({getters, commit}, {name, value, valueTo}) {
    if(value == null) return false;
    
    const oldValue = getters.dataModel.filters[name].previousValue;
    const newValue = value;
    const oldValueTo = getters.dataModel.filters[name].previousValueTo;
    const newValueTo = valueTo;
    const removedValues = _.difference(oldValue, newValue);
    const addedValues = _.difference(newValue, oldValue);

    // init unselectedFilterValues if doesn't exists
    getters.shareState.unselectedFilterValues[name] = getters.shareState.unselectedFilterValues[name] || [];
    
    // add removed value to the cached list
    if(removedValues.length > 0) {
      getters.shareState.unselectedFilterValues[name] = _.union(getters.shareState.unselectedFilterValues[name], removedValues);
    }
    
    // remove from cached list
    if(addedValues.length > 0) {
      _.remove(getters.shareState.unselectedFilterValues[name], function(value) {
        return addedValues.includes(value)
      });
    }
    if(WyFilter.compareValues(newValue, oldValue) && WyFilter.compareValues(newValueTo, oldValueTo)) {
      commit('getFilterChangedStatus', true);
      return true;
    }
    else {
      commit('getFilterChangedStatus', false);
      return false;
    }
  },

  async applyFilterValue({getters, commit}, {name, value, valueTo}) {
    if(value == null) return false;
    
    const oldValue = getters.dataModel.filters[name].previousValue;
    const newValue = value;
    const oldValueTo = getters.dataModel.filters[name].previousValueTo;
    const newValueTo = valueTo;
    const removedValues = _.difference(oldValue, newValue);
    const addedValues = _.difference(newValue, oldValue);

    // init unselectedFilterValues if doesn't exists
    getters.shareState.unselectedFilterValues[name] = getters.shareState.unselectedFilterValues[name] || [];
    
    // add removed value to the cached list
    if(removedValues.length > 0) {
      getters.shareState.unselectedFilterValues[name] = _.union(getters.shareState.unselectedFilterValues[name], removedValues);
    }
    
    // remove from cached list
    if(addedValues.length > 0) {
      _.remove(getters.shareState.unselectedFilterValues[name], function(value) {
        return addedValues.includes(value)
      });
    }

    if(!WyFilter.compareValues(newValue, oldValue) || !WyFilter.compareValues(newValueTo, oldValueTo)) {
      getters.dataModel.filters[name].hasChanged = true;
      getters.dataModel.filters[name].previousValue = _.cloneDeep(getters.dataModel.filters[name].value);
      if(getters.dataModel.filters[name].valueTo) {
        getters.dataModel.filters[name].previousValueTo = _.cloneDeep(getters.dataModel.filters[name].valueTo);
      }

      commit('updateChanged');
      return true;
    }
    else {
      getters.dataModel.filters[name].hasChanged = false;

      commit('updateChanged');
      return false;
    }
  },

  async applyDateValue({getters, commit}) {
    getters.dataModel.dateStart = getters.dataModel.dateCalendar.dateRange.start;
    getters.dataModel.dateEnd = getters.dataModel.dateCalendar.dateRange.end;
    getters.dataModel.dateHasChanged = true;
    commit('updateChanged');
  },

  async undoFilterValue({getters, commit}, {name, names}) {
    if(names && typeof names === 'object' && Array.isArray(names)) {
      for(const name of names) {
        if(getters.dataModel.filters[name]) {
          if(getters.dataModel.filters[name].previousValue != null) getters.dataModel.filters[name].value = _.cloneDeep(getters.dataModel.filters[name].previousValue);
          if(getters.dataModel.filters[name].previousValueTo != null) getters.dataModel.filters[name].valueTo = _.cloneDeep(getters.dataModel.filters[name].previousValueTo);
        }
      } 
    }
    else if(name) {
      if(getters.dataModel.filters[name]) {
        if(getters.dataModel.filters[name].previousValue) getters.dataModel.filters[name].value = _.cloneDeep(getters.dataModel.filters[name].previousValue);
        if(getters.dataModel.filters[name].previousValueTo) getters.dataModel.filters[name].valueTo = _.cloneDeep(getters.dataModel.filters[name].previousValueTo);
      }
    }

    commit('updateChanged');
  },

  async resetFilterValue({getters, commit}, {name, names}) {
    if(names && typeof names === 'object' && Array.isArray(names)) {
      for(const name of names) {
        if(getters.dataModel.filters[name]) {
          getters.dataModel.filters[name].value = _.clone(getters.dataModel.filters[name].valueOrigin);
          getters.dataModel.filters[name].valueTo = _.clone(getters.dataModel.filters[name].valueOriginTo);
        }
      } 
    }
    else if(name) {
      if(getters.dataModel.filters[name]) {
        getters.dataModel.filters[name].value = _.clone(getters.dataModel.filters[name].valueOrigin);
        getters.dataModel.filters[name].valueTo = _.clone(getters.dataModel.filters[name].valueOriginTo);
      }
    }

    commit('updateChanged');
  },

  // TODO: need to wire this to access service
  async updatePagePanels({getters}, panels) {
    try {
      let userData = localStorage.getItem('userData');
      if(!userData) {
        userData = {};
      }
      else {
        userData = JSON.parse(userData);
      }

      if(!userData[getters.currentPageName]) {
        userData[getters.currentPageName] = {};
      }

      let userDataCurrentPage = userData[getters.currentPageName];
      userDataCurrentPage.panels = panels

      localStorage.setItem('userData', JSON.stringify(userData));

    }
    catch(e) {
      console.error(e);
    }
  },

  updateShareState({ commit }, keyValuPair) {
    if(keyValuPair && keyValuPair.key) {
      commit('updateShareState', keyValuPair);
    }
  },
}

function createDataModelObj() {
  return {    
    appliedFiltersCounter: 0,
    keyword: null,
    fieldName: null,
    sortField: null,
    sortDirection: null,
    filters: null,
    language: 'en',
    platforms: null,
    dateStart: null,
    dateEnd: null,
    previousDateStart: null,
    previousDateEnd: null,
    dateRangeInDays: 0,
    dateStartCompare: null,
    dateEndCompare: null,
    calendarRangeInDays: 0,
    calendarStart: null,
    calendarEnd: null,
    currFilterNames: [],
    dateMin: null,
    dateMax: null,
    dateRangeOptions: null,
    dateRangeType: -1,
    dateCalendar: {
      dateRange: {
        start: '',
        end: ''
      }
    },
    sortings: [],
    columns: [],
    actions: [],
    page: {
      size: 25,
      number: 1      
    }
  }
}

function createPageModel() {
  return {
    dataModel: createDataModelObj(),
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}